// Hideo - Settings: device preferences, the app version, the start pages, and the profile settings edited on the
// Settings page (checklist items of the plan, video formats). The page is src/pages/SettingsPage.vue; the device
// preferences live in the settings store (src/stores/settings.ts).
// Device preferences: localStorage "creatorOS_settings" (never rename) = { startTab, autoSaveMin, navCollapsed, tlSize,
// tlImage, calSize, calImage } (+ any other key found there,
// kept as it is). Theme, main colour and language keep their own keys (creatorOS_theme, creatorOS_accent: src/features/theme.ts;
// creatorOS_lang: src/i18n). Profile preferences are in data.settings and travel with the profile.
import { LOCALE, t } from '../i18n'
import type { PageId } from '../navigation'
import type { LangPref } from '../i18n'
import type { ProfileData } from '../data/types'
import { FORMAT_NAME_MAX } from './formats'
import { moveItem } from './dragSort'
import type { CardImage, CardSize } from './cardLook'

/** Version shown in Settings › Info and in the footer (format YYYY.MM.DD.n); bump it with every release (and add its entry to CHANGELOG, ./changelog.ts) */
export const APP_VERSION = '2026.11.04.3'

export const SETTINGS_KEY = 'creatorOS_settings'

export interface DeviceSettings {
  /** Page shown after choosing the profile when the app opens */
  startTab: string
  /** 0 = online autosave off, anything else = online save right after every change */
  autoSaveMin: number
  /** Computer sidebar reduced to its icons (SideNav.vue) */
  navCollapsed: boolean
  /** Timeline bars and Calendar cards: size and image (src/features/cardLook.ts; read with cardSize / cardImage) */
  tlSize: CardSize
  tlImage: CardImage
  calSize: CardSize
  calImage: CardImage
  [extra: string]: unknown
}

export const SETTINGS_DEF: Readonly<DeviceSettings> = { startTab: 'home', autoSaveMin: 1, navCollapsed: false,
  tlSize: 'm', tlImage: 'side', calSize: 'm', calImage: 'side' }

/** Device preferences from their stored text (missing or broken: the defaults) */
export function parseSettings(raw: string | null): DeviceSettings {
  try {
    return { ...SETTINGS_DEF, ...(JSON.parse(raw as string) || {}) }
  } catch {
    return { ...SETTINGS_DEF }
  }
}

/** Pages that can be the start page, with their label */
export const START_TABS: readonly (readonly [PageId, string])[] = [['home', t('Home')], ['ped', t('Plan · Board')], ['calendar', t('Plan · Calendar')],
  ['timeline', t('Plan · Timeline')], ['published', t('Published videos')], ['vault', t('Tools · Hooks')], ['creators', t('Creators')], ['sources', t('Sources')], ['saved', t('Saved videos')], ['formats', t('Formats')]]

/** Label of the start page (the first one when the stored page is not in the list) */
export const startTabName = (tab: string): string => (START_TABS.find(([x]) => x === tab) || START_TABS[0])[1]

/** Language choices: language names are always written in their own language; only "Automatic" is translated */
export const LANG_CHOICES: readonly (readonly [LangPref, string])[] = [['auto', t('Automatic')], ['it', 'Italiano'], ['en', 'English']]

/** Size in bytes → "0 KB", "12 KB", "1,5 MB" */
export const fmtSize = (n: number): string => (n >= 1048576
  ? (n / 1048576).toLocaleString(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' MB'
  : n ? Math.max(1, Math.round(n / 1024)) + ' KB' : '0 KB')

// ===== Plan checklist (per profile): the items in the window of every video and in the progress bar of the cards =====

/** Renames item i (at most 30 characters). False when the new name is empty or the item is gone (nothing changed). */
export function renameChecklistItem(d: ProfileData, i: number, value: string): boolean {
  const v = String(value || '').trim().slice(0, 30), item = d.settings.checklist[i]
  if (!v || !item) return false
  item.label = v
  return true
}

/** Adds an item with a default name (in the UI language: it becomes user content) */
export function addChecklistItem(d: ProfileData, label: string, now = Date.now()): void {
  d.settings.checklist.push({ id: 'ck_' + now, label })
}

/** Moves item `from` to index `to` (drag to reorder in Settings › Plan): the order of the video window and of the cards'
 *  progress bar. False when nothing changed. */
export const moveChecklistItem = (d: ProfileData, from: number, to: number): boolean => moveItem(d.settings.checklist, from, to)

/** Deletes the item with this label (looked up again: the list may have changed while the user was confirming).
 *  The last item is never deleted. */
export function deleteChecklistItem(d: ProfileData, label: string): boolean {
  const list = d.settings.checklist, j = list.findIndex(x => x.label === label)
  if (j === -1 || list.length < 2) return false
  list.splice(j, 1)
  return true
}

// ===== Video formats of a project ("series" in the data) =====

type FormatRename = 'ok' | 'empty' | 'taken'

/** Renames the format at position i of data.series (at most 40 characters). 'taken' = another format of the same project
 *  has that name (case-insensitive); 'empty' = no name or no format. */
export function renameFormat(d: ProfileData, i: number, value: string): FormatRename {
  const s = d.series[i], v = String(value || '').trim().slice(0, FORMAT_NAME_MAX)
  if (!s || !v) return 'empty'
  if (d.series.some((x, j) => j !== i && x.projectId === s.projectId && x.name.toLowerCase() === v.toLowerCase())) return 'taken'
  s.name = v
  return 'ok'
}

/** Deletes a format; its videos are left without a format (they are not deleted). False when it is gone. */
export function deleteFormat(d: ProfileData, id: string): boolean {
  const i = d.series.findIndex(x => x.id === id)
  if (i === -1) return false
  d.videos.forEach(v => { if (v.seriesId === id) v.seriesId = '' })
  d.series.splice(i, 1)
  return true
}
